/**
 * Importació de la relació ARTICLE ↔ PROVEÏDOR (`supplier_articles`).
 *
 * Una fila del full és un enllaç: aquest article, aquest proveïdor, amb quina
 * referència i descripció el coneix ell, a quin preu, en quina unitat de compra,
 * i si és el principal. Un article pot tenir-ne 0..N.
 *
 * NOMÉS ENLLAÇA: NO CREA RES
 * ---------------------------
 * Ni articles, ni proveïdors, ni conversions d'unitat. Els tres han d'existir
 * abans (l'ordre de càrrega és proveïdors → articles → aquesta relació →
 * existències). Un codi que no hi és atura la fila i el diu pel seu nom, en
 * comptes de crear un article fantasma que després ningú sabria d'on ha sortit.
 *
 * EL PREU VA A LA UNITAT BASE DE L'ARTICLE
 * -----------------------------------------
 * `supplier_articles.last_price` és el preu per unitat BASE, i és el que ensenya
 * la fitxa de l'article. Un full que digués el preu per sac i una pantalla que
 * digués el preu per quilo serien dos números diferents per a la mateixa cosa,
 * i tard o d'hora algú compararia el que no toca. La plantilla ho porta escrit a
 * la capçalera de la columna.
 *
 * LA UNITAT DE COMPRA ÉS UN NOM, NO UNA MIDA
 * -------------------------------------------
 * Obrava separa la unitat («Sac») de la seva mida, que viu a la conversió de
 * CADA article («Sac» = 25 kg per a la farina, 10 kg per al sucre). Per això el
 * full porta el NOM de la unitat i no un codi com «SAC25»: la mida ja la sap
 * l'article. Escriure-hi el codi de la unitat base («kg») vol dir comprar a
 * granel. Deixar la cel·la EN BLANC no vol dir cap de les dues coses: vol dir
 * que el full no en parla, i llavors no es toca el que ja hi ha.
 *
 * Pur: sense I/O, sense rellotge, sense atzar.
 */

import {
  findExclusiveFlagConflicts,
  normalizeMasterCode,
  projectMasterRow,
  type ExclusiveFlagClaim,
  type MasterImportField,
  type MasterProjectionLabels,
  type MasterRowProjection,
} from './masterImport.js';

/** L'espai de noms i18n dels missatges d'aquest importador. */
export const SUPPLIER_ARTICLE_IMPORT_NS = 'backoffice.supplierArticleImport';

/**
 * Un enllaç article ↔ proveïdor, aplanat com el compara l'importador.
 *
 * `purchase_unit` hi és com a NOM (el de la unitat d'envàs, o el codi de la
 * unitat base quan es compra a granel) i no com a id: així la diferència que
 * l'usuari llegeix a la revisió —«Canvia: Unitat de compra, de Sac a Caixa»— és
 * literalment la que es compara, i no una parella d'UUID que no diuen res.
 */
export interface SupplierArticleImportRecord {
  readonly supplier_ref: string | null;
  readonly supplier_description: string | null;
  /** Preu per unitat BASE de l'article. */
  readonly last_price: number | null;
  readonly purchase_unit: string;
  readonly is_primary: boolean;
}

/** Les claus importables. */
export type SupplierArticleFieldKey =
  | 'article_code'
  | 'supplier_code'
  | 'supplier_ref'
  | 'supplier_description'
  | 'last_price'
  | 'purchase_unit'
  | 'purchase_unit_factor'
  | 'is_primary';

function field(
  key: SupplierArticleFieldKey,
  kind: MasterImportField['kind'],
  options: { readonly required?: boolean; readonly readOnly?: boolean } = {},
): MasterImportField<SupplierArticleFieldKey> {
  return {
    key,
    target: key,
    kind,
    required: options.required ?? false,
    labelKey: `${SUPPLIER_ARTICLE_IMPORT_NS}:field_${key}`,
    ...(options.readOnly ? { readOnly: true } : {}),
  };
}

/**
 * Els camps, en l'ordre de la plantilla. Obligatoris només els dos codis: sense
 * ells la fila no sap què enllaça amb què. `purchase_unit_factor` és
 * INFORMATIU — la plantilla amb dades hi escriu quant val una unitat de compra
 * per a aquell article, perquè qui l'omple no hagi d'anar a buscar-ho, però mai
 * s'importa: la conversió és de l'article, no d'aquesta relació.
 */
export const SUPPLIER_ARTICLE_FIELDS: readonly MasterImportField<SupplierArticleFieldKey>[] =
  [
    field('article_code', 'text', { required: true }),
    field('supplier_code', 'text', { required: true }),
    field('supplier_ref', 'text'),
    field('supplier_description', 'text'),
    field('last_price', 'number'),
    field('purchase_unit', 'text'),
    field('purchase_unit_factor', 'text', { readOnly: true }),
    field('is_primary', 'boolean'),
  ];

/**
 * Els camps que identifiquen l'enllaç: mai formen part d'una diferència. Canviar
 * un dels dos codis no és editar aquest enllaç, és parlar d'un altre.
 */
export const SUPPLIER_ARTICLE_MATCH_FIELDS: readonly string[] = [
  'article_code',
  'supplier_code',
  // Informativa: mai entra a `provided`, però excloure-la deixa el contracte
  // escrit allà on algú el buscarà.
  'purchase_unit_factor',
];

/** Llegeix una fila del full. */
export function projectSupplierArticleRow(
  cells: Readonly<Partial<Record<SupplierArticleFieldKey, string>>>,
  labels: MasterProjectionLabels = {},
): MasterRowProjection {
  return projectMasterRow(SUPPLIER_ARTICLE_FIELDS, cells, {
    namespace: SUPPLIER_ARTICLE_IMPORT_NS,
    boolean: labels,
  });
}

/**
 * La identitat d'un enllaç: la parella de codis, normalitzada. Cadena buida quan
 * en falta algun (i llavors la fila no enllaça res i no xoca amb ningú).
 */
export function supplierArticleIdentity(row: {
  readonly article_code?: string | null;
  readonly supplier_code?: string | null;
}): string {
  const article = normalizeMasterCode(row.article_code);
  const supplier = normalizeMasterCode(row.supplier_code);
  if (article === '' || supplier === '') return '';
  return `${article}|${supplier}`;
}

/** Una fila que demana ser el proveïdor principal d'un article. */
export interface PrimaryClaim {
  readonly rowNumber: number;
  /** El codi de l'article, normalitzat. */
  readonly articleKey: string;
  readonly isPrimary: boolean;
}

/**
 * Les files que demanen ser principals quan una altra del MATEIX full ja ho
 * demanava per al mateix article. Vegeu {@link findExclusiveFlagConflicts}: la
 * base de dades només admet un principal actiu per article
 * (supplier_articles_one_primary_uidx).
 */
export function findPrimaryConflicts(
  claims: readonly PrimaryClaim[],
): ReadonlyMap<number, number> {
  const generic: ExclusiveFlagClaim[] = claims.map((c) => ({
    rowNumber: c.rowNumber,
    groupKey: c.articleKey,
    flag: c.isPrimary,
  }));
  return findExclusiveFlagConflicts(generic);
}

/**
 * Converteix els camps aplanats en columnes de `supplier_articles`.
 *
 * Dues traduccions: `is_primary` (booleà) passa a `link_role`, que és com la
 * base de dades diu el mateix; i `purchase_unit` (un nom) passa a
 * `purchase_unit_id`, que el cridant ha hagut de resoldre abans contra les
 * conversions d'aquell article — `null` vol dir a granel, per unitat base.
 * Els codis no hi surten: són com s'ha trobat la fila, no dades seves.
 */
export function supplierArticleColumnsFromProvided(
  provided: Readonly<Record<string, unknown>>,
  purchaseUnitId: string | null | undefined,
): Readonly<Record<string, unknown>> {
  const columns: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(provided)) {
    if (
      key === 'article_code' ||
      key === 'supplier_code' ||
      key === 'purchase_unit_factor' ||
      key === 'purchase_unit' ||
      key === 'is_primary'
    ) {
      continue;
    }
    columns[key] = value;
  }
  if ('is_primary' in provided) {
    columns.link_role = provided.is_primary === true ? 'primary' : 'alternative';
  }
  if ('purchase_unit' in provided) {
    columns.purchase_unit_id = purchaseUnitId ?? null;
  }
  return columns;
}

/** Totes les claus i18n que aquest importador pot emetre. */
export const SUPPLIER_ARTICLE_IMPORT_MESSAGE_KEYS: readonly string[] = [
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgRequiredMissing`,
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgInvalidBoolean`,
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgInvalidNumber`,
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgUnknownArticle`,
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgUnknownSupplier`,
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgUnknownUnit`,
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgUnitNotForArticle`,
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgDuplicateInFile`,
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgTwoPrimaries`,
  `${SUPPLIER_ARTICLE_IMPORT_NS}:msgPrimaryMoves`,
];
